import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ScanLine, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const nav = useNavigate();
  const { setUserFromGoogle } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const invalid = !token;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("La password deve avere almeno 8 caratteri");
      return;
    }
    if (password !== confirm) {
      toast.error("Le password non coincidono");
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/auth/reset-password", { token, password });
      if (r.data?.user) setUserFromGoogle(r.data.user);
      toast.success("Password aggiornata. Benvenuto!");
      nav("/app");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossibile reimpostare la password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative">
        <img
          src="https://images.pexels.com/photos/4258830/pexels-photo-4258830.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="paper documents"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0A0A0A]/25 mix-blend-multiply" />
        <div className="relative h-full flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white flex items-center justify-center rounded-xl">
              <ScanLine className="w-4 h-4 text-[#0A0A0A]" />
            </div>
            <span className="font-display font-bold text-lg">Archivio Smart</span>
          </div>
          <div>
            <div className="overline text-white/70 mb-3">— NUOVA PASSWORD</div>
            <p className="font-display text-3xl leading-tight max-w-sm">
              Ricomincia da qui. Sicuro e in un istante.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 bg-[#F8F8F6]">
        <div className="w-full max-w-sm">
          <div className="overline text-[#0A0A0A] mb-3">— IMPOSTA PASSWORD</div>
          <h1 className="font-display font-bold text-4xl tracking-tighter mb-2">Nuova password.</h1>
          <p className="text-sm text-[#5C5C5C] mb-10">
            Scegli una password di almeno 8 caratteri. La useremo per accedere subito.
          </p>

          {invalid ? (
            <div
              data-testid="reset-invalid-token"
              className="rounded-xl border border-black/10 bg-white p-5 text-sm"
            >
              <p className="font-medium text-[#0A0A0A] mb-1">Link non valido o scaduto.</p>
              <p className="text-[#5C5C5C]">
                Richiedi un nuovo link dalla pagina{" "}
                <Link to="/forgot-password" className="text-[#0A0A0A] underline">
                  Password Dimenticata
                </Link>.
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5">
              <div>
                <Label htmlFor="password" className="text-xs uppercase tracking-widest">
                  Nuova password (min. 8 caratteri)
                </Label>
                <Input
                  data-testid="reset-password-input"
                  id="password"
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-xl mt-2 bg-white h-11 focus-visible:ring-[#0A0A0A]"
                />
              </div>
              <div>
                <Label htmlFor="confirm" className="text-xs uppercase tracking-widest">Conferma password</Label>
                <Input
                  data-testid="reset-password-confirm-input"
                  id="confirm"
                  type="password"
                  required
                  minLength={8}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="rounded-xl mt-2 bg-white h-11 focus-visible:ring-[#0A0A0A]"
                />
              </div>
              <Button
                data-testid="reset-submit-btn"
                disabled={loading}
                type="submit"
                className="w-full rounded-xl bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white h-11"
              >
                {loading ? "Salvo…" : (<>Salva e accedi <ArrowRight className="ml-2 w-4 h-4" /></>)}
              </Button>
            </form>
          )}

          <div className="mt-8 text-sm text-[#5C5C5C]">
            <Link data-testid="reset-goto-login" to="/login" className="text-[#0A0A0A] hover:underline">
              Torna al login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
